/**
 * Formatting for the enterprise surface: numbers, dates, names.
 *
 * Three standing P3 rules are encoded here rather than left to callers.
 *
 *  1. THE PLUS CONVENTION. Percentages take a LEADING plus (+90%); counts take
 *     a TRAILING plus (1,000+). `formatPercentChange` and `formatCountAtLeast`
 *     are the two functions that carry it. A share is not a change, so
 *     `formatPercent` does not sign anything: "+68% of your students" would be
 *     wrong.
 *  2. NO EM DASHES in anything user-facing. Ranges read "Aug 4 to Sep 1", not
 *     with a dash glyph. Every string this file produces is checked against
 *     that rule.
 *  3. STUDENTS ARE SHOWN AS "Kaelynn C." on any surface an institution sees.
 *     `abbreviateLastName` is ported verbatim from p3-web, where it is the
 *     display layer for the same rule.
 *
 * Nothing here formats a raw metric. Numbers arrive as `Cell<T>` and are
 * rendered by the Cell components in primitives.tsx, which call these
 * functions on the value inside. See src/lib/suppression.ts.
 */

const LOCALE = "en-US";

/** P3 operates from Chicago; every institutional timestamp is stamped in CT
 *  unless the caller names another zone. */
export const DEFAULT_TIME_ZONE = "America/Chicago";

/* ==========================================================================
   Numbers
   ========================================================================== */

/**
 * A headcount, grouped. 1284 becomes "1,284".
 *
 * Zero is a real, publishable answer and formats as "0". It is the caller's
 * job to know whether zero is a measurement ("no students are waiting for a
 * match") or an absence of measurement, and to render a state card for the
 * second case. Those two render identically and mean opposite things.
 */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 }).format(n);
}

/**
 * A floor, with the trailing plus: 1000 becomes "1,000+".
 *
 * Used where a count is a lower bound and P3 says so, for instance a claimed
 * roster, which is a floor rather than a register because students self-select
 * their institution at sign-up.
 */
export function formatCountAtLeast(n: number): string {
  return `${formatCount(n)}+`;
}

/**
 * A share, from a fraction in [0, 1]. 0.6825 becomes "68%".
 *
 * Unsigned on purpose: this is a proportion, not a change. Rounded to whole
 * percents by default, because a decimal place on a rate over a 40-person
 * cohort claims a precision the denominator cannot support.
 */
export function formatPercent(
  fraction: number,
  options?: { decimals?: number },
): string {
  if (!Number.isFinite(fraction)) return "";
  const decimals = options?.decimals ?? 0;
  return `${(fraction * 100).toFixed(decimals)}%`;
}

/**
 * A change, with the leading plus: +18%, -4%, 0%.
 *
 * `fraction` is the change itself (0.18), not the new value. A change is only
 * shippable beside a stated comparison period, which is why `DeltaPill`
 * requires one.
 */
export function formatPercentChange(
  fraction: number,
  options?: { decimals?: number },
): string {
  if (!Number.isFinite(fraction)) return "";
  const decimals = options?.decimals ?? 0;
  const pct = fraction * 100;
  const rounded = Number(pct.toFixed(decimals));
  if (rounded === 0) return `0%`;
  const sign = rounded > 0 ? "+" : "−"; // U+2212 MINUS, not a hyphen
  return `${sign}${Math.abs(rounded).toFixed(decimals)}%`;
}

/** "up" | "down" | "flat" for a change, so the pill and its accent tile agree. */
export function changeDirection(fraction: number): "up" | "down" | "flat" {
  if (!Number.isFinite(fraction) || fraction === 0) return "flat";
  return fraction > 0 ? "up" : "down";
}

/** "17 of 25 matched students". The denominator is never optional. */
export function formatFraction(
  numerator: number,
  denominator: number,
  noun?: string,
): string {
  const base = `${formatCount(numerator)} of ${formatCount(denominator)}`;
  return noun ? `${base} ${noun}` : base;
}

/**
 * A 95% adjusted-Wald (Agresti-Coull) interval, as whole percents.
 *
 * Any rate whose denominator is under 100 renders its interval, because on a
 * 25-person cohort "68%" alone is a claim the sample cannot carry. Showing the
 * interval reads as rigor to a provost; hiding it reads as marketing, and this
 * audience can tell the difference.
 *
 * Adjusted Wald rather than plain Wald because plain Wald is badly wrong at
 * small n and at proportions near 0 or 1, which is exactly where P3's early
 * numbers sit.
 */
export function adjustedWaldInterval(
  numerator: number,
  denominator: number,
): { low: number; high: number } | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) return null;
  if (denominator <= 0 || numerator < 0 || numerator > denominator) return null;
  const z = 1.959963985; // two-sided 95%
  const zz = z * z;
  const nAdj = denominator + zz;
  const pAdj = (numerator + zz / 2) / nAdj;
  const half = z * Math.sqrt((pAdj * (1 - pAdj)) / nAdj);
  return {
    low: Math.max(0, pAdj - half),
    high: Math.min(1, pAdj + half),
  };
}

/** "95% CI 49 to 83". No dash glyph, per the standing copy rule. */
export function formatInterval(interval: { low: number; high: number }): string {
  const low = Math.round(interval.low * 100);
  const high = Math.round(interval.high * 100);
  return `95% CI ${low} to ${high}`;
}

/** The threshold at which a rate is labelled Directional rather than reported. */
export const DIRECTIONAL_DENOMINATOR = 30;
/** The threshold at which a rate must show its confidence interval. */
export const INTERVAL_DENOMINATOR = 100;

/** US dollars, whole. Used on the gateway's ledger only; the dashboard states
 *  no dollar figures, because P3 holds no cost data. */
export function formatMoney(amount: number, options?: { cents?: boolean }): string {
  if (!Number.isFinite(amount)) return "";
  const cents = options?.cents ?? false;
  return new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  }).format(amount);
}

/* ==========================================================================
   Dates
   ========================================================================== */

export type DateInput = Date | number | string;

function toDate(input: DateInput): Date | null {
  const d =
    input instanceof Date
      ? input
      : typeof input === "number"
        ? new Date(input)
        : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * "Sep 7, 2026". Pass `style: "long"` for "September 7, 2026" and
 * `style: "short"` for "Sep 7".
 */
export function formatDate(
  input: DateInput,
  options?: { style?: "short" | "medium" | "long"; timeZone?: string },
): string {
  const d = toDate(input);
  if (!d) return "";
  const style = options?.style ?? "medium";
  const timeZone = options?.timeZone ?? DEFAULT_TIME_ZONE;
  const base: Intl.DateTimeFormatOptions =
    style === "short"
      ? { month: "short", day: "numeric" }
      : style === "long"
        ? { month: "long", day: "numeric", year: "numeric" }
        : { month: "short", day: "numeric", year: "numeric" };
  return new Intl.DateTimeFormat(LOCALE, { ...base, timeZone }).format(d);
}

/**
 * "Aug 4 to Sep 1, 2026". The word "to", never a dash glyph.
 *
 * Every reporting window on this surface is stated with both ends, because a
 * period named only by its length ("last 90 days") cannot be reproduced by a
 * reader checking the number six weeks later.
 */
export function formatDateRange(
  start: DateInput,
  end: DateInput,
  options?: { timeZone?: string },
): string {
  const a = toDate(start);
  const b = toDate(end);
  if (!a || !b) return "";
  const timeZone = options?.timeZone ?? DEFAULT_TIME_ZONE;
  const sameYear =
    new Intl.DateTimeFormat(LOCALE, { year: "numeric", timeZone }).format(a) ===
    new Intl.DateTimeFormat(LOCALE, { year: "numeric", timeZone }).format(b);
  const from = sameYear
    ? formatDate(a, { style: "short", timeZone })
    : formatDate(a, { style: "medium", timeZone });
  const to = formatDate(b, { style: "medium", timeZone });
  return `${from} to ${to}`;
}

/**
 * "2 hours ago", "yesterday", "3 weeks ago".
 *
 * Freshness only. The two words this surface bans for describing data currency
 * appear nowhere on it: the panel is refreshed on a cadence, it is not
 * streamed, and a dated stamp is what a data governance office actually
 * wants.
 */
export function formatRelative(input: DateInput, now: DateInput = Date.now()): string {
  const then = toDate(input);
  const base = toDate(now);
  if (!then || !base) return "";
  const seconds = Math.round((then.getTime() - base.getTime()) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });

  if (abs < 45) return "just now";
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), "hour");
  if (abs < 7 * 86400) return rtf.format(Math.round(seconds / 86400), "day");
  if (abs < 30 * 86400) return rtf.format(Math.round(seconds / (7 * 86400)), "week");
  if (abs < 365 * 86400) return rtf.format(Math.round(seconds / (30 * 86400)), "month");
  return rtf.format(Math.round(seconds / (365 * 86400)), "year");
}

/**
 * "As of Sep 7, 2026, 4:12 PM CT".
 *
 * The header's freshness stamp, and the line on every printed page. It reads
 * from the data layer's real timestamp, never from a string literal, and it is
 * the honest replacement for the streaming language this surface bans.
 *
 * The zone renders in its generic short form ("CT", not "CDT"), because a
 * board reader in March should not have to work out whether the number was
 * taken before or after a clock change to know it is the same zone.
 */
export function asOfStamp(
  input: DateInput,
  options?: { timeZone?: string; prefix?: string },
): string {
  const d = toDate(input);
  if (!d) return "";
  const timeZone = options?.timeZone ?? DEFAULT_TIME_ZONE;
  const prefix = options?.prefix ?? "As of";
  const date = new Intl.DateTimeFormat(LOCALE, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  }).format(d);
  const time = new Intl.DateTimeFormat(LOCALE, {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "shortGeneric",
    timeZone,
  }).format(d);
  return `${prefix} ${date}, ${time}`;
}

/**
 * The Sunday on or before a date, at 00:00 in the given zone's calendar day.
 *
 * Every time window on this surface ends on the previous Sunday, never on
 * today. A partial current week renders as a cliff, and every reader reads a
 * cliff as a collapse. The rule is enforced in code so no chart can opt out.
 */
export function previousSunday(input: DateInput = Date.now()): Date {
  const d = toDate(input);
  if (!d) return new Date(Number.NaN);
  const out = new Date(d.getTime());
  out.setHours(0, 0, 0, 0);
  const day = out.getDay(); // 0 = Sunday
  out.setDate(out.getDate() - day);
  return out;
}

/* ==========================================================================
   Names
   ========================================================================== */

/**
 * Display rule ported from p3-web: people's names render with each part
 * capitalized, regardless of how they were entered. The app accepts free-text
 * names and some arrive lower-cased, so "thierry henry" must show as "Thierry
 * Henry".
 *
 * Only the first letter of each part (after a start, space, hyphen, or
 * apostrophe) is upper-cased; the rest is left untouched so intentional inner
 * caps survive. "DeShawn", "O'Brien" and "Jean-Luc" are kept.
 */
export function capitalizeName(name: string): string {
  return name.replace(
    /(^|[\s'-])([a-z])/g,
    (_match, boundary: string, letter: string) => boundary + letter.toUpperCase(),
  );
}

/**
 * Ported from p3-web, where it masks a mentee's name for a mentor. The same
 * rule governs this surface: an institution sees "Kaelynn C.", never a full
 * last name and never contact information.
 *
 * This is a display layer. Full enforcement, the API returning masked names,
 * is a backend concern; masking here means a screenshot of this dashboard
 * cannot leak a roster.
 */
export function abbreviateLastName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return capitalizeName(fullName.trim());
  const last = parts[parts.length - 1] ?? "";
  const initial = last.charAt(0).toUpperCase();
  const firstNames = capitalizeName(parts.slice(0, -1).join(" "));
  return initial ? `${firstNames} ${initial}.` : firstNames;
}

/* ==========================================================================
   Lists
   ========================================================================== */

/**
 * "and 3 more schools". The count is always COMPUTED from the data, never
 * written into copy, so a capped list cannot drift from what it capped.
 */
export function formatMore(hiddenCount: number, noun: string, pluralNoun?: string): string {
  const word = hiddenCount === 1 ? noun : (pluralNoun ?? `${noun}s`);
  return `and ${formatCount(hiddenCount)} more ${word}`;
}

/** "Career guidance, college applications and networking". Oxford comma off,
 *  matching P3's house style in prose. */
export function formatList(items: readonly string[]): string {
  return new Intl.ListFormat(LOCALE, { style: "long", type: "conjunction" }).format(
    items as string[],
  );
}

/** "student" / "students", so no string in the UI reads "1 students". */
export function pluralize(n: number, singular: string, plural?: string): string {
  return n === 1 ? singular : (plural ?? `${singular}s`);
}
